import React, { useEffect, useMemo, useState } from "react";
import { Loader2, TrendingUp, DollarSign, Percent, Wallet, FileDown } from "lucide-react";
import api, { money } from "../lib/api";
import ListFilters from "../components/ListFilters";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell } from "recharts";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";

const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const axisFmt = (v) => (Math.abs(v) >= 1000 ? `Q${(v / 1000).toFixed(1)}k` : `Q${Math.round(v)}`);

export default function Profitability() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [vendor, setVendor] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/profitability?date_from=${from}&date_to=${to}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [from, to]);

  const vendors = useMemo(() => (data?.by_vendor || []).map((v) => v.vendor), [data]);
  const invoices = useMemo(() => {
    const rows = data?.invoices || [];
    return vendor === "all" ? rows : rows.filter((r) => r.vendor_name === vendor);
  }, [data, vendor]);
  const s = data?.summary || { sales: 0, cost: 0, profit: 0, margin: 0, count: 0 };

  const cards = [
    { label: "Ventas (sin IVA)", value: money("GTQ", s.sales), Icon: DollarSign, cls: "text-blue-500 border-blue-500/20 bg-blue-500/10" },
    { label: "Costo total", value: money("GTQ", s.cost), Icon: Wallet, cls: "text-amber-500 border-amber-500/20 bg-amber-500/10" },
    { label: "Utilidad", value: money("GTQ", s.profit), Icon: TrendingUp, cls: `${s.profit < 0 ? "text-red-500 border-red-500/20 bg-red-500/10" : "text-green-500 border-green-500/20 bg-green-500/10"}` },
    { label: "Margen", value: pct(s.margin), Icon: Percent, cls: `${s.margin < 0 ? "text-red-500 border-red-500/20 bg-red-500/10" : "text-green-500 border-green-500/20 bg-green-500/10"}` },
  ];

  const exportCSV = () => {
    const headers = ["Factura", "Cliente", "Vendedor", "Fecha", "Moneda", "Ventas", "Costo", "Utilidad", "Margen%"];
    const lines = invoices.map((r) => [r.number, r.client_name, r.vendor_name, r.date, r.currency, r.sales, r.cost, r.profit, r.margin]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = ["\ufeff" + headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rentabilidad-${new Date(Date.now()-216e5).toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-sm border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        Rentabilidad calculada solo sobre facturas <strong className="text-foreground">costeadas</strong> (con costo asignado a sus ítems). Costéalas desde el detalle de cada factura con el botón "Costear".
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="prof-cards">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-xl border p-4 ${c.cls}`}>
            <div className="flex items-center gap-2 mb-2"><c.Icon className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-widest">{c.label}</span></div>
            <div className="text-2xl font-bold font-mono">{c.value}</div>
          </div>
        ))}
      </div>

      <ListFilters searchTestId="prof-search" search="" onSearch={() => {}} placeholder=""
        from={from} to={to} onFrom={setFrom} onTo={setTo}
        vendors={vendors} vendor={vendor} onVendor={setVendor}
        onClear={() => { setFrom(""); setTo(""); setVendor("all"); }}
        right={<Button variant="outline" className="rounded-sm gap-2 text-xs h-9" onClick={exportCSV} data-testid="prof-export"><FileDown className="h-4 w-4" /> Exportar CSV</Button>} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <>
        <div className="grid lg:grid-cols-2 gap-4" data-testid="prof-charts">
          <Card className="rounded-sm border shadow-none overflow-hidden">
            <p className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border">Utilidad por vendedor</p>
            <div className="p-4 h-72" data-testid="prof-chart-vendors">
              {(data?.by_vendor || []).length === 0 ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Sin datos.</div> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_vendor} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="vendor" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={axisFmt} />
                    <Tooltip formatter={(v) => money("GTQ", v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))" }} />
                    <Bar dataKey="profit" name="Utilidad" radius={[4, 4, 0, 0]}>
                      {data.by_vendor.map((v, i) => <Cell key={i} fill={v.profit < 0 ? "#dc2626" : "#F26A00"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          <Card className="rounded-sm border shadow-none overflow-hidden">
            <p className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border">Tendencia mensual: Ventas vs. Utilidad</p>
            <div className="p-4 h-72" data-testid="prof-chart-months">
              {(data?.by_month || []).length === 0 ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Sin datos.</div> : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.by_month} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={axisFmt} />
                    <Tooltip formatter={(v) => money("GTQ", v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))" }} />
                    <Line type="monotone" dataKey="sales" name="Ventas" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="profit" name="Utilidad" stroke="#F26A00" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-5 gap-4">
          <Card className="rounded-sm border shadow-none overflow-hidden lg:col-span-2">
            <p className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border">Utilidad por vendedor</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="prof-vendors">
                <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground"><th className="px-3 py-2">Vendedor</th><th className="px-3 py-2 text-right"># Fac.</th><th className="px-3 py-2 text-right">Ventas</th><th className="px-3 py-2 text-right">Utilidad</th><th className="px-3 py-2 text-right">Margen</th></tr></thead>
                <tbody>
                  {(data?.by_vendor || []).length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">Sin datos.</td></tr> :
                    data.by_vendor.map((v) => (
                      <tr key={v.vendor} className="border-t border-border">
                        <td className="px-3 py-2 font-medium">{v.vendor}</td>
                        <td className="px-3 py-2 text-right">{v.count}</td>
                        <td className="px-3 py-2 text-right font-mono">{money("GTQ", v.sales)}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${v.profit < 0 ? "text-red-600" : "text-green-700"}`}>{money("GTQ", v.profit)}</td>
                        <td className={`px-3 py-2 text-right font-mono ${v.margin < 0 ? "text-red-600" : "text-green-700"}`}>{pct(v.margin)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="rounded-sm border shadow-none overflow-hidden lg:col-span-3">
            <p className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border">Facturas costeadas ({invoices.length})</p>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="prof-invoices">
                <thead><tr className="bg-muted text-left text-[10px] uppercase text-muted-foreground sticky top-0"><th className="px-3 py-2">Factura</th><th className="px-3 py-2">Cliente</th><th className="px-3 py-2 text-right">Ventas</th><th className="px-3 py-2 text-right">Costo</th><th className="px-3 py-2 text-right">Utilidad</th><th className="px-3 py-2 text-right">Margen</th></tr></thead>
                <tbody>
                  {invoices.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">Sin facturas costeadas en este rango.</td></tr> :
                    invoices.map((r) => (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/50" data-testid={`prof-row-${r.number}`}>
                        <td className="px-3 py-2"><div className="font-mono text-xs font-bold text-primary">{r.number}</div><div className="text-[10px] text-muted-foreground">{r.vendor_name}</div></td>
                        <td className="px-3 py-2">{r.client_name}</td>
                        <td className="px-3 py-2 text-right font-mono">{money(r.currency, r.sales)}</td>
                        <td className="px-3 py-2 text-right font-mono">{money(r.currency, r.cost)}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${r.profit < 0 ? "text-red-600" : "text-green-700"}`}>{money(r.currency, r.profit)}</td>
                        <td className={`px-3 py-2 text-right font-mono ${r.margin < 0 ? "text-red-600" : "text-green-700"}`}>{pct(r.margin)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
        </>
      )}
    </div>
  );
}
